import { useMemo } from 'react'

import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { getAppsByGroup } from 'shared/configs/appConfigs'
import { AppConfig } from 'shared/types/app'

import { AppGroupData } from '../LaunchPad.utils'
import { LAUNCHPAD_APP_GROUPS } from '../Launchpad.config'

export interface UseLaunchPadAppsReturn {
  appsByGroup: Array<AppGroupData>
  isAppLocked: (app: AppConfig) => boolean
  isAppSpotlighted: (app: AppConfig) => boolean
  spotlightApps: AppConfig[]
  isLoading: boolean
}

export const useLaunchPadApps = (): UseLaunchPadAppsReturn => {
  const { apps: availableApps, isLoading } = useAvailableApps()
  const appsByGroup = useMemo(
    () =>
      LAUNCHPAD_APP_GROUPS.map(group =>
        getAppsByGroup(Object.values(availableApps), group, 'launchpad')
      ),
    [availableApps]
  )

  const isAppLocked = useMemo(
    () => (app: AppConfig) =>
      Object.values(availableApps).some(
        availableApp => availableApp.id === app.id && availableApp.isDisabled
      ),
    [availableApps]
  )

  const spotlightApps = useMemo(
    () =>
      appsByGroup
        .flatMap(groupData => Object.values(groupData).flat())
        .filter((app): app is AppConfig => Boolean(app?.isSpotlighted)),
    [appsByGroup]
  )

  const isAppSpotlighted = (app: AppConfig) => Boolean(app.isSpotlighted)

  return {
    appsByGroup,
    isAppLocked,
    isAppSpotlighted,
    spotlightApps,
    isLoading
  }
}
